import { z } from 'zod'
import { requiredString, timestampedDocSchema } from '../utils/basicSchemas/document.z.schema'
import { mongoId, mongoIdSchema } from '../utils/basicSchemas/_id.z.schema'
import { ZSchema } from '../utils/zodSchema'

/**
 * Enum representing the types of reports. 
 * 
 * @example
 * const reportType = ReportType.parse("post");
 */
const ReportType = z.enum(["user", "post"], { required_error: "Report type is required" })

/**
 * Enum representing the possible reasons for a report.
 * 
 * @example
 * const reportReason = ReportReason.parse("Harrasment or bullying");
 */
const ReportReason = z.enum([
    "Harrasment or bullying",
    "Nudity or pornography",
    "Hate speech or symbols",
    "Sales or promotion or firearms",
    "Self Injury",
    "Violence",
    "I just don't like it"
], { required_error: "Report reason is required" })


/**
 * Schema definition for a report.
 * Contains details about the report such as type, item ID, creator, reason, and optional description.
 * 
 * @example
 * const myReport = reportSchema.parse({
 *   type: "post",
 *   itemId: "123456",
 *   reason: "Harrasment or bullying",
 * });
 */
const reportSchema = z.object({
    type: ReportType,
    itemId: requiredString("Report item id"),
    creator: mongoId("Creator user's id"),
    reason: ReportReason,
    description: z.string().nullable().default(null).optional(),
})

/**
 * Zod schema representing a report document.
 * 
 * This schema extends {@link reportSchema} by merging with {@link timestampedDocSchema} 
 * to include fields inherent to Mongoose documents like timestamps, `_id` and `__v`.
 * 
 * @example
 * const myReportDoc = reportSchemaDoc.parse({
 *   _id: "603f650a0953de235c89eadc",
 *   __v: "1",
 *   createdAt: new Date(),
 *   updatedAt: new Date(),
 *   type: "post",
 *   itemId: "123456",
 *   reason: "Harrasment or bullying",
 * });
 */
const reportSchemaDoc = timestampedDocSchema.merge(reportSchema)

// Type inference exports
type ReportTypeEnum = z.infer<typeof ReportType>
type ReportReasonEnum = z.infer<typeof ReportReason>

type ReportSchemaType = z.infer<typeof reportSchema>
type ReportSchemaDocType = z.infer<typeof reportSchemaDoc>

// Instance and export Validation objects 

// TODO: UNTESTED: No ui to trigger reporting 
const reportCreationValidator = new ZSchema({ bodySchema: reportSchema })

export { reportSchemaDoc, reportSchema }
export { ReportSchemaType, ReportSchemaDocType }
export { ReportTypeEnum, ReportReasonEnum }
export { reportCreationValidator }
